import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";

import { router } from "./router";
import { installTenantHeaderInterceptor } from "./lib/apiClient";
import { initTheme } from "./hooks/useTheme";
// Inicializa i18n (pt-BR default, detecção via localStorage) antes do React montar.
// O import é síncrono — bundles são estáticos, sem carregamento assíncrono. PSHWO-56859.
import "./i18n";
import "./index.css";

// Aplica tema antes do React montar — evita flash of unstyled content.
initTheme();
installTenantHeaderInterceptor();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Erros 4xx são falha de contrato cliente↔servidor (validação,
      // permissão, recurso inexistente) — retentar só amplifica o problema
      // e polui o console. Mantém retry para 5xx/network (default 3). TH-55119.
      retry: (failureCount, error) => {
        const match = error instanceof Error
          ? error.message.match(/^HTTP (\d+)/)
          : null;
        const status = match ? Number(match[1]) : 0;
        if (status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {import.meta.env.DEV && (
        <>
          <ReactQueryDevtools initialIsOpen={false} />
          <TanStackRouterDevtools router={router} />
        </>
      )}
    </QueryClientProvider>
  </StrictMode>,
);
